import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminListLeads, adminUpdateLead, adminStats, adminNotifications } from "../lib/api";
import { toast } from "sonner";
import { Hammer, LogOut, RefreshCw, X, Bell, DollarSign, Calendar, Filter } from "lucide-react";

const STATUSES = ["new", "contacted", "scheduled", "inspected", "quoted", "won", "lost"];

const STATUS_COLORS = {
  new: "bg-sky-100 text-sky-800 border-sky-200",
  contacted: "bg-amber-100 text-amber-800 border-amber-200",
  scheduled: "bg-violet-100 text-violet-800 border-violet-200",
  inspected: "bg-indigo-100 text-indigo-800 border-indigo-200",
  quoted: "bg-emerald-100 text-emerald-800 border-emerald-200",
  won: "bg-green-200 text-green-900 border-green-300",
  lost: "bg-slate-200 text-slate-700 border-slate-300",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = typeof window !== "undefined" ? localStorage.getItem("cbr_admin_token") : null;

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    refresh();
    // eslint-disable-next-line
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const [l, s, n] = await Promise.all([adminListLeads(), adminStats(), adminNotifications()]);
      setLeads(l);
      setStats(s);
      setNotifs(n);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("cbr_admin_token");
        navigate("/admin/login");
      } else {
        toast.error("Failed to load");
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("cbr_admin_token");
    navigate("/admin/login");
  };

  const updateStatus = async (lead, status) => {
    try {
      const updated = await adminUpdateLead(lead.id, { status });
      setLeads((cur) => cur.map((x) => (x.id === lead.id ? updated : x)));
      if (selected?.id === lead.id) setSelected(updated);
      toast.success(`Marked ${status}`);
    } catch {
      toast.error("Update failed");
    }
  };

  const filtered = useMemo(() => (filter === "all" ? leads : leads.filter((l) => l.status === filter)), [leads, filter]);

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }} data-testid="admin-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "var(--fg)" }}>
              <Hammer className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-display text-lg font-semibold tracking-tight">Cash Bid CRM</div>
              <div className="text-xs text-slate-500">{localStorage.getItem("cbr_admin_email")}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={refresh} className="px-3 py-2 rounded-lg text-sm border border-slate-200 hover:bg-slate-50 flex items-center gap-2" data-testid="refresh-button">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button onClick={logout} className="px-3 py-2 rounded-lg text-sm border border-slate-200 hover:bg-slate-50 flex items-center gap-2 text-slate-700 hover:text-slate-900" data-testid="logout-button">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard label="Total leads" value={stats?.total ?? "—"} testId="stat-total" />
          <StatCard label="New" value={stats?.by_status?.new ?? 0} testId="stat-new" />
          <StatCard label="Scheduled" value={stats?.by_status?.scheduled ?? 0} testId="stat-scheduled" icon={<Calendar className="w-4 h-4" />} />
          <StatCard label="Pipeline value" value={stats ? `$${Math.round(stats.pipeline_value).toLocaleString()}` : "—"} testId="stat-pipeline" icon={<DollarSign className="w-4 h-4" />} />
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="status-filter-bar">
          <Filter className="w-4 h-4 text-slate-400" />
          <FilterChip active={filter === "all"} onClick={() => setFilter("all")} testId="filter-all">All ({leads.length})</FilterChip>
          {STATUSES.map((s) => (
            <FilterChip key={s} active={filter === s} onClick={() => setFilter(s)} testId={`filter-${s}`}>
              {s} ({stats?.by_status?.[s] ?? 0})
            </FilterChip>
          ))}
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Leads table */}
          <div className="lg:col-span-8 bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="font-display text-lg font-semibold">Leads</h2>
              <div className="text-xs text-slate-500">{filtered.length} shown</div>
            </div>
            {loading ? (
              <div className="p-12 text-center text-slate-400">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="p-12 text-center text-slate-400" data-testid="empty-leads">No leads yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="leads-table">
                  <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="px-5 py-3 text-left">Homeowner</th>
                      <th className="px-5 py-3 text-left">Property</th>
                      <th className="px-5 py-3 text-left">AI Bid</th>
                      <th className="px-5 py-3 text-left">Status</th>
                      <th className="px-5 py-3 text-left">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((l) => (
                      <tr
                        key={l.id}
                        onClick={() => setSelected(l)}
                        className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                        data-testid={`lead-row-${l.id}`}
                      >
                        <td className="px-5 py-3">
                          <div className="font-medium text-slate-900">{l.full_name}</div>
                          <div className="text-xs text-slate-500">{l.email} · {l.phone}</div>
                        </td>
                        <td className="px-5 py-3 text-slate-700">{l.address}</td>
                        <td className="px-5 py-3 text-slate-900 font-medium">
                          ${Math.round(l.ai_estimate_low || 0).toLocaleString()} – ${Math.round(l.ai_estimate_high || 0).toLocaleString()}
                        </td>
                        <td className="px-5 py-3">
                          <span className={`inline-block px-2 py-1 rounded text-xs font-medium border ${STATUS_COLORS[l.status] || ""}`}>{l.status}</span>
                        </td>
                        <td className="px-5 py-3 text-xs text-slate-500">{new Date(l.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Notifications */}
          <div className="lg:col-span-4">
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
                <Bell className="w-4 h-4 text-amber-600" />
                <h2 className="font-display text-lg font-semibold">Notifications</h2>
              </div>
              <div className="max-h-[600px] overflow-y-auto" data-testid="notifications-list">
                {notifs.length === 0 ? (
                  <div className="p-8 text-center text-slate-400 text-sm">No notifications yet.</div>
                ) : (
                  notifs.map((n) => (
                    <div key={n.id} className="px-5 py-3 border-b border-slate-100 text-sm" data-testid={`notif-${n.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs uppercase tracking-wider font-semibold text-amber-600">{n.channel}</span>
                        <span className="text-xs text-slate-400">{new Date(n.created_at).toLocaleTimeString()}</span>
                      </div>
                      <div className="text-slate-700">{n.message}</div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lead drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-slate-900/40 backdrop-blur-sm" onClick={() => setSelected(null)} data-testid="lead-drawer">
          <div className="w-full max-w-xl bg-white h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white/95 backdrop-blur">
              <h3 className="font-display text-xl font-semibold">{selected.full_name}</h3>
              <button onClick={() => setSelected(null)} className="p-2 rounded hover:bg-slate-100" data-testid="close-drawer-button">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Status</div>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map((s) => (
                    <button
                      key={s}
                      onClick={() => updateStatus(selected, s)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${selected.status === s ? STATUS_COLORS[s] : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
                      data-testid={`set-status-${s}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <Detail label="Contact" rows={[["Email", selected.email], ["Phone", selected.phone]]} />
              <Detail label="Property" rows={[["Address", selected.address], ["City / Zip", `${selected.city || "—"} ${selected.zip_code || ""}`]]} />
              <Detail label="Roof details" rows={[
                ["Type", selected.roof_type || "—"],
                ["Age", selected.roof_age || "—"],
                ["Sq ft", selected.square_footage || "—"],
                ["Has solar", selected.has_solar ? `Yes (${selected.solar_panel_count || "?"} panels)` : "No"],
                ["Damage notes", selected.damage_notes || "—"],
              ]} />
              <Detail label="AI Bid" rows={[
                ["Range", `$${Math.round(selected.ai_estimate_low || 0).toLocaleString()} – $${Math.round(selected.ai_estimate_high || 0).toLocaleString()}`],
                ["Summary", selected.ai_summary || "—"],
              ]} />
              <Detail label="Appointment" rows={[
                ["Date", selected.appointment_date || "Not scheduled"],
                ["Slot", selected.appointment_slot || "—"],
              ]} />
              {selected.photos && selected.photos.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Photos ({selected.photos.length})</div>
                  <div className="grid grid-cols-3 gap-2">
                    {selected.photos.map((p, i) => (
                      <img key={i} src={p} alt={`roof-${i}`} className="w-full h-24 object-cover rounded border border-slate-200" />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 lift-on-hover" data-testid={testId}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">
        {icon} {label}
      </div>
      <div className="font-display text-3xl font-semibold mt-2 text-slate-900">{value}</div>
    </div>
  );
}

function FilterChip({ children, active, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize border transition ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

function Detail({ label, rows }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">{label}</div>
      <div className="bg-slate-50 rounded-lg border border-slate-200 divide-y divide-slate-200">
        {rows.map(([k, v]) => (
          <div key={k} className="px-4 py-2.5 flex justify-between gap-4 text-sm">
            <span className="text-slate-500">{k}</span>
            <span className="text-slate-900 font-medium text-right">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
